import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { TextLink } from '@/components/atoms/link';
import { SlidingTabs } from '@/components/atoms/tabs';
import { MeetingSummarySheet } from '@/components/molecules/meetings/MeetingSummarySheet';
import { formatDate } from '@/formatters/formatDate';
import { useTranslation } from '@/i18n';
import { type GetActivitiesQuery } from '@/generated/usermobile-bff/graphql';
import { radii, spacing, unisonColors, useAppTheme } from '@/theme';

type ActivityItem = GetActivitiesQuery['activities']['data'][number];
type MeetingActivity = ActivityItem & { readonly meeting: NonNullable<ActivityItem['meeting']> };

type MeetingCardTab = 'summary' | 'notes';

const COLLAPSED_LINES = 5;

interface HandoffMeetingActivityCardProps {
  readonly activity: MeetingActivity;
}

export const HandoffMeetingActivityCard = ({ activity }: HandoffMeetingActivityCardProps) => {
  const { t, locale } = useTranslation();
  const { colors } = useAppTheme();
  const [tab, setTab] = useState<MeetingCardTab>('summary');
  const [sheetVisible, setSheetVisible] = useState(false);

  const handleOpenSheet = useCallback(() => {
    setSheetVisible(true);
  }, []);

  const handleDismissSheet = useCallback(() => {
    setSheetVisible(false);
  }, []);

  const { meeting } = activity;

  const attendeeNames = meeting.attendees
    .map((attendee) => attendee.contact?.displayName)
    .filter((name): name is string => name != null && name !== '');

  const meetingName = meeting.name ?? t('aiTasks.detail.meetingCard.title');
  const userName = meeting.user?.name ?? '';

  const description =
    attendeeNames.length === 0
      ? t('aiTasks.detail.meetingCard.descriptionNoAttendees', {
          user: userName,
          meeting: meetingName,
        })
      : t('aiTasks.detail.meetingCard.description', {
          user: userName,
          attendees: attendeeNames.join('、'),
          meeting: meetingName,
        });

  const summaryText = meeting.recording?.summary?.summaryText ?? null;
  const notesText = meeting.notes ?? null;
  const activeText = tab === 'summary' ? summaryText : notesText;
  const emptyKey =
    tab === 'summary'
      ? 'aiTasks.detail.meetingCard.noSummary'
      : 'aiTasks.detail.meetingCard.noNotes';

  const tabItems = useMemo(
    () => [
      { key: 'summary', label: t('aiTasks.detail.meetingCard.summaryTab') },
      { key: 'notes', label: t('aiTasks.detail.meetingCard.notesTab') },
    ],
    [t],
  );

  const handleSelectTab = useCallback((key: string) => {
    setTab(key as MeetingCardTab);
  }, []);

  return (
    <View testID={`handoff-meeting-card-${activity.id}`}>
      <View style={styles.card}>
        <View style={styles.header}>
          <Text style={{ color: colors.textNdPrimary }} variant="titleSmall">
            {t('aiTasks.detail.meetingCard.title')}
          </Text>
          <Text style={{ color: colors.textNdSecondary }} variant="bodySmall">
            {formatDate(meeting.startsAt, locale)}
          </Text>
        </View>
        <Text style={[styles.description, { color: colors.textNdPrimary }]}>{description}</Text>
        <SlidingTabs
          activeKey={tab}
          items={tabItems}
          testID={`handoff-meeting-card-${activity.id}-tabs`}
          variant="inline"
          onSelect={handleSelectTab}
        />
        {activeText == null ? (
          <Text style={[styles.body, { color: colors.textNdSecondary }]}>{t(emptyKey)}</Text>
        ) : (
          <>
            <Text
              numberOfLines={COLLAPSED_LINES}
              style={[styles.body, { color: colors.textNdPrimary }]}>
              {activeText}
            </Text>
            <TextLink
              flush
              label={t('aiTasks.detail.meetingCard.readMore')}
              testID={`handoff-meeting-card-${activity.id}-read-more`}
              onPress={handleOpenSheet}
            />
          </>
        )}
      </View>
      {activeText == null ? null : (
        <MeetingSummarySheet
          summary={activeText}
          title={meetingName}
          visible={sheetVisible}
          onDismiss={handleDismissSheet}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: unisonColors.white,
    borderRadius: radii.md,
    flex: 1,
    padding: spacing[4],
    rowGap: spacing[2],
  },
  header: {
    alignItems: 'center',
    columnGap: spacing[3],
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  description: {
    fontSize: 14,
    letterSpacing: 0.25,
    lineHeight: 22,
  },
  body: {
    fontSize: 14,
    letterSpacing: 0.25,
    lineHeight: 22,
  },
});
